import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";

const links = [
  { href: "#about", label: "About" },
  { href: "#who-we-help", label: "Who We Help" },
  { href: "#services", label: "Services" },
  { href: "#why-us", label: "Why Everwell" },
  { href: "#referrals", label: "Referrals" },
  { href: "#contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-[#6E9C88]/15"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <button
            data-testid="nav-logo"
            onClick={() => scrollTo("#top")}
            className="flex items-center gap-2 group"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--ew-accent)] text-white font-heading font-semibold">
              E
            </span>
            <span className="font-heading text-lg sm:text-xl font-semibold tracking-tight text-[var(--ew-text)] group-hover:text-[var(--ew-accent)] transition-colors">
              Everwell
              <span className="hidden sm:inline text-[var(--ew-text-muted)] font-normal">
                {" "}
                Community Nursing
              </span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => scrollTo(l.href)}
                className="font-body text-sm text-[var(--ew-text-2)] hover:text-[var(--ew-accent)] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:0450270547"
              data-testid="nav-phone"
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--ew-text-2)] hover:text-[var(--ew-accent)] transition-colors"
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} />
              0450 270 547
            </a>
            <button
              data-testid="nav-referral-btn"
              onClick={() => scrollTo("#referrals")}
              className="btn-primary text-sm py-2.5 px-5"
            >
              Make a Referral
            </button>
          </div>

          <button
            data-testid="nav-menu-toggle"
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/70 border border-[#6E9C88]/20 text-[var(--ew-accent)]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div
            data-testid="nav-mobile-menu"
            className="lg:hidden pb-6 pt-2 border-t border-[#6E9C88]/15 mt-1 bg-white/95 backdrop-blur-xl"
          >
            <ul className="flex flex-col gap-2 pt-3">
              {links.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => scrollTo(l.href)}
                    className="w-full text-left px-2 py-3 rounded-xl hover:bg-[var(--ew-bg-3)] font-body text-[var(--ew-text)]"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex gap-3 pt-3">
              <a
                href="tel:0450270547"
                className="btn-secondary flex-1 text-sm py-2.5"
              >
                <Phone className="h-4 w-4" /> Call
              </a>
              <button
                onClick={() => scrollTo("#referrals")}
                className="btn-primary flex-1 text-sm py-2.5"
              >
                Make a Referral
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
